<script setup lang="ts">
import type { Product } from '@/services/product'
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer'
import { computed, watch } from 'vue'

import { formatMoneyWithCurrency } from '@/lib/formatters/money'
import { extractShopifyId } from '@/lib/gid'
import { getOriginalPrice, getPrice } from '@/lib/sellingPlans'
import { track } from '@/lib/track'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'

import Button from '@/components/Button.vue'
import Label from '@/components/Label.vue'
import ProductImage from '@/components/ProductImage.vue'

const props = defineProps<{
  title: string
  product?: Product
  loadState: AsyncOperationState
  workState?: AsyncOperationState
  section?: string
  customerName?: string
  reasons?: Record<string, string>
  offerPercents?: Record<string, number>
  introText?: string | null
}>()

const emit = defineEmits<{
  (e: 'add-to-next-order', product: Product): void
}>()

const reason = computed(() => {
  if (!props.product) return undefined
  return props.reasons?.[extractShopifyId(props.product.id)]
})

const offerPercent = computed(() => {
  if (!props.product) return undefined
  return props.offerPercents?.[extractShopifyId(props.product.id)]
})

const productHref = (product: Product) =>
  `/product/${extractShopifyId(product.id)}?target=browse`

const recommendationProperties = (product: Product) => ({
  section: props.section,
  product_id: extractShopifyId(product.id),
  product_title: product.title,
  offer_percent: props.offerPercents?.[extractShopifyId(product.id)] ?? null,
})

watch(
  () =>
    props.loadState === AsyncOperationState.DONE
      ? props.product?.id
      : undefined,
  (productId) => {
    if (!productId || !props.section || !props.product) return
    track(
      'recommendation_product_viewed',
      recommendationProperties(props.product),
    )
  },
  { immediate: true },
)

const onProductClick = (product: Product) => {
  if (!props.section) return
  track('recommendation_product_clicked', recommendationProperties(product))
}

const onAddToNextOrder = (product: Product) => {
  if (props.workState === AsyncOperationState.IN_PROGRESS) return
  if (props.section) {
    track(
      'recommendation_add_to_next_order_clicked',
      recommendationProperties(product),
    )
  }
  emit('add-to-next-order', product)
}

const subscriptionPrice = (product: Product) =>
  getPrice(
    product,
    product.variants.edges[0].node.id,
    product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges[0]?.node.id ??
      null,
  )

const originalPrice = (product: Product) =>
  getOriginalPrice(product, product.variants.edges[0].node.id)

const introPrice = (product: Product) => {
  const percent = props.offerPercents?.[extractShopifyId(product.id)]
  if (!percent) return null
  return Math.round(subscriptionPrice(product) * (1 - percent / 100))
}

const discountedFirstCharge = computed(() =>
  props.product ? introPrice(props.product) : null,
)

const eyebrow = computed(() =>
  props.customerName ? `${props.title} ${props.customerName}` : props.title,
)

const introHtml = computed(() => {
  if (!props.introText) return null
  try {
    return convertSchemaToHtml(props.introText)
  } catch {
    return null
  }
})
</script>

<template>
  <div class="FeaturedProduct">
    <template v-if="loadState === AsyncOperationState.DONE && product">
      <div class="FeaturedProduct__Product">
        <a
          class="FeaturedProduct__ImageLink"
          :href="productHref(product)"
          @click="onProductClick(product)"
        >
          <ProductImage
            class="FeaturedProduct__Image"
            :alt="product.title"
            :url="product.images.edges[0]?.node?.small"
          ></ProductImage>
          <Label
            v-if="discountedFirstCharge != null && offerPercent"
            mode="warning-alt"
            class="Label--small FeaturedProduct__OfferLabel"
          >
            {{ offerPercent }}% off
          </Label>
        </a>
        <div class="FeaturedProduct__Copy">
          <p class="FeaturedProduct__Eyebrow">{{ eyebrow }}</p>
          <div class="FeaturedProduct__ProductDetails">
            <div class="FeaturedProduct__ProductTitle">
              <a :href="productHref(product)" @click="onProductClick(product)">
                <!-- eslint-disable vue/no-v-html -->
                <span
                  v-if="introHtml"
                  class="FeaturedProduct__IntroText"
                  v-html="introHtml"
                ></span>
                <!-- eslint-enable vue/no-v-html -->
                <template v-else>{{ product.title }}</template>
              </a>
            </div>
            <div class="FeaturedProduct__PriceContainer">
              <template v-if="discountedFirstCharge != null">
                <span class="FeaturedProduct__Price">
                  {{ formatMoneyWithCurrency(discountedFirstCharge / 100) }}
                </span>
                <span class="FeaturedProduct__PriceOriginal">
                  {{
                    formatMoneyWithCurrency(subscriptionPrice(product) / 100)
                  }}
                </span>
              </template>
              <template v-else>
                <span class="FeaturedProduct__Price">
                  {{
                    formatMoneyWithCurrency(subscriptionPrice(product) / 100)
                  }}
                </span>
                <span
                  v-if="originalPrice(product) > subscriptionPrice(product)"
                  class="FeaturedProduct__PriceOriginal"
                >
                  {{ formatMoneyWithCurrency(originalPrice(product) / 100) }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
      <p v-if="reason" class="FeaturedProduct__Reason">{{ reason }}</p>
      <div class="FeaturedProduct__Actions">
        <Button
          class="FeaturedProduct__Add"
          size="large"
          :loading="workState === AsyncOperationState.IN_PROGRESS"
          :disabled="
            workState === AsyncOperationState.IN_PROGRESS ||
            !product.availableForSale
          "
          @click="onAddToNextOrder(product)"
        >
          Add to next order
        </Button>
      </div>
    </template>
    <template v-else>
      <div class="FeaturedProduct__Product">
        <ProductImage
          class="FeaturedProduct__Image"
          alt="Loading..."
        ></ProductImage>
        <div class="FeaturedProduct__Copy">
          <p class="FeaturedProduct__Eyebrow">{{ eyebrow }}</p>
          <div class="FeaturedProduct__ProductDetails">
            <p class="FeaturedProduct__ProductTitle GhostUI">Title</p>
            <div class="FeaturedProduct__PriceContainer GhostUI">
              <span class="FeaturedProduct__Price">10.00</span>
            </div>
          </div>
        </div>
      </div>
      <p class="FeaturedProduct__Reason GhostUI">Recommendation reason</p>
    </template>
  </div>
</template>

<style>
.FeaturedProduct {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 16px 12px;
  padding: 20px;
  border-radius: 16px;
  background: var(--color-white);
  border: 1px solid var(--color-lichen-100);
  align-self: stretch;
  align-items: center;
}

.FeaturedProduct__Product,
.FeaturedProduct__Copy {
  display: contents;
}

.FeaturedProduct__Eyebrow {
  grid-column: 1 / -1;
  order: -1;
  margin: 0;
  color: var(--color-ocean-100);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.3;
  text-transform: uppercase;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-right: 8px;
    border-radius: 50%;
    background: currentcolor;
    vertical-align: middle;
    transform: translateY(-1px);
  }
}

.FeaturedProduct__ImageLink,
.FeaturedProduct__Product > .FeaturedProduct__Image {
  grid-column: 1;
}

.FeaturedProduct__ImageLink {
  display: block;
  position: relative;
}

.FeaturedProduct__Image {
  display: block;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--color-white);
}

.FeaturedProduct__ProductDetails {
  grid-column: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.FeaturedProduct__ProductTitle {
  margin: 0;
  color: var(--color-forest-100);
  font-size: 24px;
  line-height: 22px;
  font-family: var(--font-family-domaine);

  & > a {
    color: inherit;
  }
}

.FeaturedProduct__IntroText p {
  margin: 0;
  line-height: 1.2;
}

.FeaturedProduct__PriceContainer {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.FeaturedProduct__Price {
  color: var(--color-forest-100);
  font-size: 20px;
  font-weight: 400;
  line-height: 20px;
}

.FeaturedProduct__PriceOriginal {
  color: var(--color-forest-60);
  font-size: 14px;
  font-weight: 400;
  text-decoration-line: line-through;
}

.FeaturedProduct__OfferLabel {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;
  pointer-events: none;
}

.FeaturedProduct__Reason {
  grid-column: 1 / -1;
  margin: 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--color-white);
  color: var(--color-forest-80);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.FeaturedProduct__Actions {
  grid-column: 1 / -1;
  display: flex;
  align-items: stretch;
}

.FeaturedProduct__Add.Button {
  width: 100%;
}

@media (--mq-tablet-and-up) {
  .FeaturedProduct {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    padding: 16px 24px 16px 16px;
  }

  .FeaturedProduct__Copy {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .FeaturedProduct__Eyebrow {
    grid-column: auto;
  }

  .FeaturedProduct__ImageLink,
  .FeaturedProduct__Product > .FeaturedProduct__Image {
    grid-column: auto;
    flex-shrink: 0;
  }

  .FeaturedProduct__Image {
    width: 160px;
    height: 160px;
    border-radius: 12px;
  }

  .FeaturedProduct__ProductDetails {
    grid-column: auto;
    gap: 8px;
  }

  .FeaturedProduct__ProductTitle {
    line-height: 1.15;
  }

  .FeaturedProduct__Reason {
    display: none;
  }

  .FeaturedProduct__Actions {
    grid-column: auto;
    flex-shrink: 0;
  }

  .FeaturedProduct__Add.Button {
    width: auto;
    white-space: nowrap;
    padding-inline: 28px;
  }
}
</style>
